import { type ReactNode, useEffect } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Icon } from './icon'

interface ModalProps {
	children: ReactNode
	isOpen: boolean
	onClose: () => void
	showCloseButton?: boolean
	rounded?: boolean
	maxWidth?: 'default' | 'large' | 'xl' | number
}

export function Modal({
	children,
	isOpen,
	onClose,
	showCloseButton = true,
	rounded = true,
	maxWidth = 'default',
}: ModalProps) {
	// Get width class based on maxWidth prop
	const getWidthClass = () => {
		if (typeof maxWidth === 'number') {
			return `md:w-[${maxWidth}px]`
		}
		switch (maxWidth) {
			case 'large':
				return 'md:w-[1024px]'
			case 'xl':
				return 'md:w-[1440px]'
			case 'default':
			default:
				return 'md:max-w-5xl'
		}
	}
	// Prevent body scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'unset'
		}
		return () => {
			document.body.style.overflow = 'unset'
		}
	}, [isOpen])

	// Close on Escape key
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOpen) {
				onClose()
			}
		}
		document.addEventListener('keydown', handleEscape)
		return () => document.removeEventListener('keydown', handleEscape)
	}, [isOpen, onClose])

	if (!isOpen) return null

	return (
		<>
			{/* Backdrop - solo visible en desktop */}
			<div
				className="fixed inset-0 z-40 hidden bg-black/50 md:block"
				onClick={onClose}
				aria-hidden="true"
			/>

			{/* Modal Content */}
			<div className="fixed inset-0 z-50 flex items-center justify-center md:p-4">
				{/* Mobile: full screen, Desktop: modal */}
				<div
					className={cn(
						'md:shadow-2 relative h-full overflow-y-auto bg-white md:h-auto md:max-h-[90vh]',
						getWidthClass(),
						rounded && 'md:rounded-3xl',
					)}
				>
					{showCloseButton && (
						<button
							onClick={onClose}
							className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-neutral-600 transition-colors hover:bg-neutral-100 md:top-6 md:right-6"
							aria-label="Cerrar"
						>
							<Icon name="cross-1" className="size-5" />
						</button>
					)}
					{children}
				</div>
			</div>
		</>
	)
}
